import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast, ShowSuccessToast } from '../services/toast';
import { FaTrash, FaExclamationCircle } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { User, Building2, Phone, Mail, MapPin, Camera, Plus, ChevronLeft, ChevronRight, Check, Heart, Clock } from 'lucide-react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

mapboxgl.accessToken = 'pk.eyJ1IjoiZG9ub3JkaXNwYXRjaCIsImEiOiJjbWMwdjZrZWIwNnU2MmxwdHBsaGkzcm9rIn0.u70VsaQCG6q1r8SLZ8nVLg';

const stripePromise = loadStripe('pk_live_51SVwRDJhi5A6bxjc2XroYceuquExdpIBriGorqShkVXVHTkZnGeVOawNkcrDnuMavn8fd8C7itKAwAE71KeCOzeJ00Ewooq3r2');

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c) * 0.621371;
};

const isPointInPolygon = (lat, lng, polygon) => {
  if (!polygon || polygon.length === 0) {
    return false;
  }
  const firstCoord = polygon[0][0];
  const isLatLngFormat = Math.abs(firstCoord) <= 90;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi, yi, xj, yj;
    if (isLatLngFormat) {
      xi = polygon[i][1];
      yi = polygon[i][0];
      xj = polygon[j][1];
      yj = polygon[j][0];
    } else {
      xi = polygon[i][0];
      yi = polygon[i][1];
      xj = polygon[j][0];
      yj = polygon[j][1];
    }
    const intersect = ((yi > lat) !== (yj > lat)) &&
      (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

const validatePhoneNumber = (phoneNumber) => {
  const cleanedNumber = phoneNumber.replace(/[\s()-]/g, '');
  const indianNumberRegex = /^\+91[6-9][0-9]{9}$/;
  const usaNumberRegex = /^\+1[0-9]{10}$/;
  return indianNumberRegex.test(cleanedNumber) || usaNumberRegex.test(cleanedNumber);
};


const DeliveryContributionForm = React.memo(({
  deliveryContribution,
  setDeliveryContribution,
  processingPayment,
  setProcessingPayment,
  paymentError,
  setPaymentError,
  createdDonationId,
  donationForm,
  setShowThankYou,
  setShowDeliveryContribution,
  ShowDangerToast,
  ShowSuccessToast,
  ApiServices
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!deliveryContribution.contribute) {
      setShowThankYou(true);
      return;
    }

    if (!stripe || !elements) {
      console.error('Stripe or Elements not loaded:', { stripe, elements });
      ShowDangerToast('Payment system not ready. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    console.log('CardElement retrieved:', cardElement);

    if (!cardElement) {
      console.error('CardElement not found');
      ShowDangerToast('Card input not found. Please refresh the page.');
      return;
    }

    if (!cardComplete) {
      console.warn('Card details incomplete. cardComplete:', cardComplete);
      ShowDangerToast('Please complete your card details');
      return;
    }

    const finalAmount = deliveryContribution.amount
      ? Math.round(parseFloat(deliveryContribution.amount) * 100)
      : parseInt(deliveryContribution.presetAmount);

    if (!finalAmount || finalAmount < 100) {
      console.error('Invalid amount:', finalAmount);
      ShowDangerToast('Please select a valid contribution amount');
      return;
    }

    setProcessingPayment(true);
    setPaymentError(null);

    try {
      const paymentRes = await ApiServices.postData('/donations/create-payment-intent', {
        amount: finalAmount,
        donationRequestId: createdDonationId,
      });

      if (!paymentRes.data?.clientSecret) {
        throw new Error('No client secret received from server');
      }

      const billingDetails = {
        name: donationForm.donorName,
        email: donationForm.email,
        phone: donationForm.phone,
      };

      const result = await stripe.confirmCardPayment(paymentRes.data.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      });

      if (result.error) {
        console.error('Payment error from Stripe:', result.error);
        console.error('Error type:', result.error.type);
        console.error('Error code:', result.error.code);
        console.error('Error message:', result.error.message);
        setPaymentError(result.error.message);
        ShowDangerToast(result.error.message);
      } else {
        console.log('Updating donation monetary status...');
        await ApiServices.postData('/donations/update-monetary-status', {
          donationRequestId: createdDonationId,
          paymentStatus: 'completed'
        });

        console.log('Donation status updated successfully');
        setShowThankYou(true);
        ShowSuccessToast(`Thank you! Your $${(finalAmount / 100).toFixed(2)} delivery contribution was successful.`);
      }
    } catch (err) {
      console.error('=== Payment Error Caught ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);

      const errorMessage = err.response?.data?.error || err.message || 'Payment failed.';
      setPaymentError(errorMessage);
      ShowDangerToast(errorMessage);
    } finally {
      setProcessingPayment(false);
      console.log('Payment process finished');
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);

    if (event.error) {
      console.warn('Card validation error:', event.error.message);
      setCardError(event.error.message);
      setPaymentError(event.error.message);
    } else {
      setCardError(null);
      setPaymentError(null);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4 py-4 sm:p-0">
      <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Goods Donation Confirmed!</h3>
          <p className="text-sm text-green-600 mt-1 font-medium">Your donation has been successfully scheduled.</p>
        </div>

        <h4 className="text-xl font-semibold mb-3 text-gray-800">Support Our Delivery Program</h4>
        <p className="text-gray-600 mb-4 text-sm sm:text-base">
          Your goods donation is already making a difference! To help us continue offering free pickup services to all donors, please consider contributing to delivery costs.
        </p>

        <div className="mb-6">
          <label className="flex items-center cursor-pointer gap-3 mb-4 p-3 bg-navy-50 rounded-lg border border-navy-200 hover:bg-navy-100 transition">
            <input
              type="checkbox"
              checked={deliveryContribution.contribute}
              onChange={(e) => {
                setDeliveryContribution({
                  ...deliveryContribution,
                  contribute: e.target.checked
                });
              }}
              className="accent-navy-500 w-5 h-5"
            />
            <div>
              <span className="text-sm sm:text-base font-medium text-gray-800">
                Yes, I'd like to contribute to delivery expenses
              </span>
              <p className="text-xs text-gray-500 mt-0.5">Every dollar helps keep our pickup service free</p>
            </div>
          </label>

          {deliveryContribution.contribute && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Contribution Amount
                </label>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                  {[
                    { value: '500', label: '$5' },
                    { value: '1000', label: '$10' },
                    { value: '2000', label: '$20' },
                    { value: '5000', label: '$50' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setDeliveryContribution({
                          ...deliveryContribution,
                          presetAmount: option.value,
                          amount: ''
                        });
                      }}
                      className={`py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition text-center ${deliveryContribution.presetAmount === option.value
                        ? 'bg-navy-500 text-white shadow-md'
                        : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                    >
                      {option.label}
                      <span className="block text-xs font-normal mt-1 opacity-75">
                        {option.note}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-row items-center gap-3 flex-wrap sm:flex-nowrap">
                  <span className="text-gray-600 text-sm sm:text-base whitespace-nowrap">Custom amount:</span>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-400 rounded-lg px-3 flex-1 sm:flex-initial">
                    <span className="text-gray-600 font-medium">$</span>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={deliveryContribution.amount}
                      onChange={(e) => {
                        setDeliveryContribution({
                          ...deliveryContribution,
                          amount: e.target.value,
                          presetAmount: ''
                        });
                      }}
                      className="w-full py-2 bg-transparent focus:outline-none text-gray-800"
                      placeholder="15.00"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Details
                </label>
                <div className="p-3 sm:p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#1f2937',
                          '::placeholder': { color: '#9ca3af' },
                        },
                        invalid: {
                          color: '#ef4444',
                        },
                      },
                      hidePostalCode: false,
                    }}
                    onChange={handleCardChange}
                  />
                </div>
                {cardError && (
                  <div className="text-orange-600 text-xs mt-1">
                    {cardError}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Card status: {cardComplete ? '✓ Complete' : '✗ Incomplete'}
                </div>
              </div>

              {paymentError && (
                <div className="text-red-600 text-center text-sm mt-2 p-2 bg-red-50 rounded">
                  {paymentError}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          {!deliveryContribution.contribute ? (
            <button
              type="button"
              onClick={() => {
                setShowThankYou(true);
                setShowDeliveryContribution(false);
                ShowSuccessToast('Thank you for your generous donation! Your items have been successfully scheduled.');
              }}
              className="px-6 py-3 rounded-lg text-sm font-semibold bg-navy-500 text-white hover:bg-navy-600 transition w-full sm:w-auto"
            >
              Complete & View Thank You
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePaymentSubmit}
              disabled={processingPayment || !stripe || !cardComplete}
              className="px-6 py-3 rounded-lg text-sm font-semibold bg-navy-500 text-white hover:bg-navy-600 transition disabled:opacity-60 w-full sm:w-auto"
            >
              {processingPayment ? (
                <>
                  <svg className="animate-spin inline-block h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : `Contribute $${(
                deliveryContribution.amount
                  ? Math.round(parseFloat(deliveryContribution.amount) * 100)
                  : parseInt(deliveryContribution.presetAmount)
              ) / 100} & Complete`}
            </button>
          )}
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Your goods donation has been confirmed. You can complete the process now or add a delivery contribution.
          </p>
        </div>
      </div>
    </div>
  );
});

const DonationRequestPage = () => {
  const { storeId } = useParams();
  const [stores, setStores] = useState([]);
  const [, setCandidateStores] = useState([]);
  const [store, setStore] = useState(null);
  const [distance, setDistance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [, setSelectedStoreId] = useState(null);

  const [showDeliveryContribution, setShowDeliveryContribution] = useState(false);
  const [createdDonationId, setCreatedDonationId] = useState(null);
  const [deliveryContribution, setDeliveryContribution] = useState({
    contribute: false,
    amount: '',
    presetAmount: '500',
  });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const [donationForm, setDonationForm] = useState({
    donorName: '',
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phone: '',
    address: '',
    pickupDate: null,
    pickUpTime: '',
    specialNote: '',
    items: [{ name: '', description: '', files: [] }],
    pickUpType: '',
    enableReminders: false,
  });

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [mapCoordinates, setMapCoordinates] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [, setFirstStorePicture] = useState(null);
  const [isWithinGeofence, setIsWithinGeofence] = useState(null);
  const [travelTimePolygon, setTravelTimePolygon] = useState([]);
  const [errors, setErrors] = useState({});
  const [itemErrors, setItemErrors] = useState([]);
  const autocompleteTimeoutRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [restrictedItem, setRestrictedItem] = useState([]);
  const [showRestrictedPopup, setShowRestrictedPopup] = useState(false);
  const [, setWelcomeNote] = useState(
    "Thank you for visiting our donation page.\nYour generosity empowers our mission and supports those who need it most. Together, we can make an impact that lasts."
  );
  const [currentStep, setCurrentStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const fileInputRefs = useRef([]);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const addressRef = useRef(null);
  const slotPickerRef = useRef(null);
  const pickupTypeRef = useRef(null);
  const itemRefs = useRef([]);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);

  const clearError = (field) => {
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  const clearItemError = (index) => {
    setItemErrors(prev => prev.filter(i => i !== index));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!donationForm.firstName.trim()) newErrors.firstName = true;
    if (!donationForm.lastName.trim()) newErrors.lastName = true;
    if (!donationForm.address.trim()) newErrors.address = true;
    if (!donationForm.email.trim()) {
      newErrors.email = true;
      ShowDangerToast('Email is required for tax receipt');
    }
    if (!store || isWithinGeofence === false) newErrors.geofence = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const focusFirstError = () => {
    if (errors.firstName && firstNameRef.current) {
      firstNameRef.current.focus();
    } else if (errors.lastName && lastNameRef.current) {
      lastNameRef.current.focus();
    } else if (errors.email && emailRef.current) {
      emailRef.current.focus();
    } else if (errors.address && addressRef.current) {
      addressRef.current.focus();
    }
  };

  const validateStep2 = () => {
    if (!donationForm.pickupDate) {
      ShowDangerToast('Please select a pickup date');
      return false;
    }
    if (!donationForm.pickUpTime) {
      ShowDangerToast('Please select a time window');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    const newItemErrors = [];
    if (!donationForm.pickUpType) {
      ShowDangerToast('Pickup type is required');
      if (pickupTypeRef.current) pickupTypeRef.current.scrollIntoView({ behavior: 'smooth' });
      return false;
    }

    const hasAtLeastOneItem = donationForm.items.some(item => item.name.trim());
    if (!hasAtLeastOneItem) {
      ShowDangerToast('Please add at least one donation item');
      return false;
    }

    donationForm.items.forEach((item, index) => {
      if (item.name.trim() && (!item.files || item.files.length === 0)) {
        newItemErrors.push(index);
      }
    });

    setItemErrors(newItemErrors);
    if (newItemErrors.length > 0) {
      ShowDangerToast('Each donation item must have at least one image');
      const firstInvalid = itemRefs.current[newItemErrors[0]];
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        focusFirstError();
        ShowDangerToast('Please fill in all required fields');
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentStep === 2 && store?.id) {
      const fetchSlots = async () => {
        setSlotsLoading(true);
        try {
          const res = await ApiServices.getData(`/donations/available-slots/${store.id}`);
          const data = {};
          (res.data || []).forEach(slot => { data[slot.date] = slot.availableSlots; });
          setAvailableSlots(data);
        } catch (err) {
          console.error('Error fetching slots', err);
        } finally {
          setSlotsLoading(false);
        }
      };
      fetchSlots();
    }
  }, [currentStep, store?.id]);

  useEffect(() => {
    const fetchAllStores = async () => {
      try {
        const response = await ApiServices.getData(`/stores/get-all-stores/${storeId}`);
        const fetchedStores = response?.data || [];
        setStores(fetchedStores);
        setLoading(false);

        if (fetchedStores.length > 0) {
          const matchedStore = fetchedStores.find(s =>
            s.storeId === storeId ||
            s.name.toLowerCase().includes(storeId.replace(/-/g, ' ')) ||
            storeId.includes(s.name.toLowerCase().replace(/ /g, '-'))
          );

          if (matchedStore) {
            setStore(matchedStore);
            setFirstStorePicture(matchedStore?.owner?.profilePicture);
            setSelectedStoreId(matchedStore.id);
            setWelcomeNote(matchedStore?.itemRestrictions || "Welcome to our donation program!");

            const restrictions = matchedStore?.itemRestriction;
            if (restrictions) {
              const parsed = Array.isArray(restrictions)
                ? restrictions
                : restrictions.split(',').map(item => item.trim());
              setRestrictedItem(parsed);
            } else {
              setRestrictedItem([]);
            }
          }
        }
      } catch (error) {
        ShowDangerToast('Failed to load stores');
        setLoading(false);
      }
    };

    if (storeId) {
      fetchAllStores();
    }
  }, [storeId]);

  useEffect(() => {
    if (stores.length > 0 && storeId) {
      const urlStore = stores.find(s => s.storeId === storeId);
      if (urlStore) {
        setWelcomeNote(urlStore.itemRestrictions || "Welcome to our donation program!");
        setFirstStorePicture(urlStore.owner.profilePicture)
      }
    }
  }, [stores, storeId]);

  useEffect(() => {
    if (!mapRef.current || !store?.latitude || !store?.longitude || mapInstanceRef.current) return;
    try {
      mapInstanceRef.current = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [store.longitude, store.latitude],
        zoom: 10,
      });
      mapInstanceRef.current.addControl(new mapboxgl.NavigationControl());
      mapInstanceRef.current.on('load', () => {
        setIsMapLoaded(true);
        updateMap();
      });
      mapInstanceRef.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        ShowDangerToast('Failed to load map');
      });
    } catch (error) {
      ShowDangerToast('Failed to initialize map');
    }
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch { }
        mapInstanceRef.current = null;
        setIsMapLoaded(false);
      }
    };
  }, [store]);

  useEffect(() => {
    if (mapInstanceRef.current && isMapLoaded && store?.latitude && store?.longitude) {
      updateMap();
    }
  }, [store, mapCoordinates, travelTimePolygon, isMapLoaded]);

  const updateMap = () => {
    if (!mapInstanceRef.current || !isMapLoaded || !store?.latitude || !store?.longitude) {
      return;
    }
    try {
      ['store-marker', 'donor-marker', 'geofence-circle-inner', 'geofence-circle-outer', 'geofence-polygon', 'geofence-polygon-outline', 'distance-line'].forEach(layer => {
        if (mapInstanceRef.current.getLayer(layer)) {
          mapInstanceRef.current.removeLayer(layer);
        }
      });
      ['store-marker', 'donor-marker', 'geofence-circle', 'geofence-polygon', 'distance-line'].forEach(source => {
        if (mapInstanceRef.current.getSource(source)) {
          mapInstanceRef.current.removeSource(source);
        }
      });
      mapInstanceRef.current.addSource('store-marker', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [store.longitude, store.latitude],
          },
          properties: {
            title: 'Store Location',
            description: store.address,
          },
        },
      });
      mapInstanceRef.current.addLayer({
        id: 'store-marker',
        type: 'circle',
        source: 'store-marker',
        paint: {
          'circle-radius': 8,
          'circle-color': '#FF671F',
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-width': 2,
        },
      });
      mapInstanceRef.current.on('click', 'store-marker', (e) => {
        new mapboxgl.Popup()
          .setLngLat(e.features[0].geometry.coordinates)
          .setHTML(`<b>${e.features[0].properties.title}</b><br>${e.features[0].properties.description}`)
          .addTo(mapInstanceRef.current);
      });
      if (store.geofenceType === 'circle' && store.radius) {
        const radiusInMeters = store.radius * 1609.34;
        const innerRadius = Math.min(radiusInMeters * 0.2, 500);
        mapInstanceRef.current.addSource('geofence-circle', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [store.longitude, store.latitude],
            },
            properties: {},
          },
        });
        mapInstanceRef.current.addLayer({
          id: 'geofence-circle-inner',
          type: 'circle',
          source: 'geofence-circle',
          paint: {
            'circle-radius': {
              stops: [[10, innerRadius / 1000], [15, innerRadius / 500]],
              base: 2,
            },
            'circle-color': '#33A7B5',
            'circle-opacity': 0.3,
          },
        });
        mapInstanceRef.current.addLayer({
          id: 'geofence-circle-outer',
          type: 'circle',
          source: 'geofence-circle',
          paint: {
            'circle-radius': {
              stops: [[10, radiusInMeters / 1000], [15, radiusInMeters / 500]],
              base: 2,
            },
            'circle-color': '#33A7B5',
            'circle-opacity': 0.2,
            'circle-stroke-color': '#003947',
            'circle-stroke-width': 3,
          },
        });
      } else if (['polygon', 'travel-time'].includes(store.geofenceType) && (store.polygon?.length > 0 || travelTimePolygon.length > 0)) {
        const polygon = store.geofenceType === 'travel-time' && travelTimePolygon.length > 0 ? travelTimePolygon : store.polygon;
        if (polygon.length > 0) {
          mapInstanceRef.current.addSource('geofence-polygon', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [polygon.map(coord => [coord[1], coord[0]])],
              },
            },
          });
          mapInstanceRef.current.addLayer({
            id: 'geofence-polygon',
            type: 'fill',
            source: 'geofence-polygon',
            paint: {
              'fill-color': '#33A7B5',
              'fill-opacity': 0.2,
            },
          });
          mapInstanceRef.current.addLayer({
            id: 'geofence-polygon-outline',
            type: 'line',
            source: 'geofence-polygon',
            paint: {
              'line-color': '#003947',
              'line-width': 3,
              'line-opacity': 0.8,
            },
          });
        }
      }
      if (mapCoordinates) {
        mapInstanceRef.current.addSource('donor-marker', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [mapCoordinates.longitude, mapCoordinates.latitude],
            },
            properties: {
              title: 'Donor Location',
              description: donationForm.address,
            },
          },
        });
        mapInstanceRef.current.addLayer({
          id: 'donor-marker',
          type: 'circle',
          source: 'donor-marker',
          paint: {
            'circle-radius': 8,
            'circle-color': isWithinGeofence ? '#33A7B5' : '#FF671F',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 2,
          },
        });
        mapInstanceRef.current.on('click', 'donor-marker', (e) => {
          new mapboxgl.Popup()
            .setLngLat(e.features[0].geometry.coordinates)
            .setHTML(`<b>${e.features[0].properties.title}</b><br>${e.features[0].properties.description}`)
            .addTo(mapInstanceRef.current);
        });
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([store.longitude, store.latitude]);
        bounds.extend([mapCoordinates.longitude, mapCoordinates.latitude]);
        mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
        mapInstanceRef.current.addSource('distance-line', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [mapCoordinates.longitude, mapCoordinates.latitude],
                [store.longitude, store.latitude],
              ],
            },
          },
        });
        mapInstanceRef.current.addLayer({
          id: 'distance-line',
          type: 'line',
          source: 'distance-line',
          paint: {
            'line-color': '#888',
            'line-width': 2,
            'line-dasharray': [2, 2],
          },
        });
      } else {
        mapInstanceRef.current.setCenter([store.longitude, store.latitude]);
        const radius = store.geofenceType === 'circle' && store.radius ? store.radius : 5;
        const bounds = new mapboxgl.LngLatBounds(
          [store.longitude - radius / 69, store.latitude - radius / 69],
          [store.longitude + radius / 69, store.latitude + radius / 69]
        );
        mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
      }
    } catch (error) {
      console.error('Error updating map:', error);
      ShowDangerToast('Failed to update map');
    }
  };

  const fetchTravelTimeGeofence = async (selectedStore, lat, lng) => {
    if (!selectedStore.travelTime) {
      return { within: false, polygon: [] };
    }
    try {
      const response = await axios.get(
        `https://api.mapbox.com/isochrone/v1/mapbox/driving/${selectedStore.longitude},${selectedStore.latitude}`,
        {
          params: {
            contours_minutes: parseInt(selectedStore.travelTime),
            polygons: true,
            access_token: mapboxgl.accessToken,
          },
        }
      );
      const coordinates = response.data.features[0].geometry.coordinates[0];
      const within = isPointInPolygon(lat, lng, coordinates);
      return { within, polygon: coordinates };
    } catch (error) {
      console.error('Travel time validation error:', error);
      return { within: false, polygon: [] };
    }
  };

  const validateGeofence = async (selectedStore, lat, lng) => {
    if (!selectedStore.latitude || !selectedStore.longitude) {
      return false;
    }
    let withinGeofence = false;
    if (selectedStore.geofenceType === 'circle' && selectedStore.radius) {
      const distance = getDistance(lat, lng, selectedStore.latitude, selectedStore.longitude);
      withinGeofence = distance <= selectedStore.radius;
    } else if (selectedStore.geofenceType === 'polygon' && selectedStore.polygon?.length > 0) {
      withinGeofence = isPointInPolygon(lat, lng, selectedStore.polygon);
    } else if (selectedStore.geofenceType === 'travel-time') {
      const result = await fetchTravelTimeGeofence(selectedStore, lat, lng);
      withinGeofence = result.within;
    }
    return withinGeofence;
  };

  const handleAddressChange = async (e) => {
    const address = e.target.value;
    setDonationForm({ ...donationForm, address });
    clearError('address');
    if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
    if (address.length < 3) {
      setAddressSuggestions([]);
      setMapCoordinates(null);
      setIsWithinGeofence(null);
      setTravelTimePolygon([]);
      setCandidateStores([]);
      setStore(null);
      setDistance(null);
      return;
    }
    autocompleteTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`, {
          params: {
            access_token: mapboxgl.accessToken,
            country: 'us',
            limit: 5,
            autocomplete: true,
            types: 'address,poi',
          },
        });
        setAddressSuggestions(response.data.features?.map(feature => ({
          formattedAddress: feature.place_name,
          latitude: feature.geometry?.coordinates?.[1],
          longitude: feature.geometry?.coordinates?.[0],
          id: feature.id,
        })) || []);
      } catch (error) {
        console.error('Autocomplete error:', error);
        if (error.response?.status === 422) {
          ShowDangerToast('Invalid address. Please try a different format.');
        }
        setAddressSuggestions([]);
      }
    }, 300);
  };

  const handleAddressSelect = async (selectedAddress) => {
    setDonationForm({ ...donationForm, address: selectedAddress.formattedAddress });
    const latitude = selectedAddress.latitude;
    const longitude = selectedAddress.longitude;
    const newCoordinates = { latitude, longitude };
    setMapCoordinates(newCoordinates);
    setAddressSuggestions([]);
    setStore(null);
    setDistance(null);
    let candidates = [];
    for (const currentStore of stores) {
      const within = await validateGeofence(currentStore, latitude, longitude);
      if (within) {
        const dist = getDistance(latitude, longitude, currentStore.latitude, currentStore.longitude);
        candidates.push({ store: currentStore, dist });
      }
    }
    if (candidates.length === 0) {
      let allStoresWithDist = stores.map(currentStore => {
        const dist = getDistance(latitude, longitude, currentStore.latitude, currentStore.longitude);
        return { store: currentStore, dist };
      });
      allStoresWithDist.sort((a, b) => a.dist - b.dist);
      const nearestOutside = allStoresWithDist[0];
      setStore(nearestOutside.store);
      setDistance(nearestOutside.dist);
      setIsWithinGeofence(false);
      setUnavailableDates(nearestOutside.store?.unavailableDates || []);
      setFirstStorePicture(nearestOutside.store?.owner?.profilePicture);
      setWelcomeNote(nearestOutside.store?.itemRestrictions || "Welcome to our donation program!");
      const restrictions = nearestOutside.store?.itemRestriction;
      if (restrictions) {
        const parsed = Array.isArray(restrictions)
          ? restrictions
          : restrictions.split(',').map(item => item.trim());
        setRestrictedItem(parsed);
      } else {
        setRestrictedItem([]);
      }
      setTravelTimePolygon([]);
      ShowDangerToast(`No stores found near this address. Contact the nearest store (${nearestOutside.store.name}) for exceptions.`);
    } else {
      candidates.sort((a, b) => a.dist - b.dist);
      setCandidateStores(candidates.slice(0, 5));
      handleStoreSelect(candidates[0], latitude, longitude);
    }
  };

  const handleStoreSelect = async (selectedCandidate, lat, lng) => {
    const nearest = selectedCandidate.store;
    const dist = selectedCandidate.dist;
    setStore(nearest);
    setDistance(dist);
    setUnavailableDates(nearest?.unavailableDates || []);
    setFirstStorePicture(nearest?.owner?.profilePicture);
    setWelcomeNote(nearest?.itemRestrictions || "Welcome to our donation program!");
    const restrictions = nearest?.itemRestriction;
    if (restrictions) {
      const parsed = Array.isArray(restrictions)
        ? restrictions
        : restrictions.split(',').map(item => item.trim());
      setRestrictedItem(parsed);
    } else {
      setRestrictedItem([]);
    }
    let polygon = [];
    if (nearest.geofenceType === 'travel-time') {
      const result = await fetchTravelTimeGeofence(nearest, lat, lng);
      polygon = result.polygon;
      setIsWithinGeofence(result.within);
    } else {
      setIsWithinGeofence(true);
    }
    setTravelTimePolygon(polygon);
  };

  const createDonationRequest = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      focusFirstError();
      ShowDangerToast('Please fill in all required fields in Step 1');
      return null;
    }

    if (!validateStep2()) {
      setCurrentStep(2);
      return null;
    }

    if (!validateStep3()) {
      return null;
    }

    if (mapCoordinates) {
      const isWithin = await validateGeofence(store, mapCoordinates.latitude, mapCoordinates.longitude);
      if (!isWithin) {
        ShowDangerToast('Address is outside the store\'s geofence');
        setCurrentStep(1);
        return null;
      }
    }

    const validPhoneNumber = validatePhoneNumber(donationForm.phone);
    if (donationForm.phone && !validPhoneNumber) {
      setErrors(prev => ({ ...prev, phone: true }));
      ShowDangerToast('Invalid Phone number');
      setCurrentStep(1);
      if (phoneRef.current) phoneRef.current.focus();
      return null;
    }

    setIsSubmitting(true);
    try {
      const year = donationForm.pickupDate.getFullYear();
      const month = String(donationForm.pickupDate.getMonth() + 1).padStart(2, '0');
      const day = String(donationForm.pickupDate.getDate()).padStart(2, '0');
      const formData = new FormData();
      formData.append('storeId', store.id);
      formData.append('donorName', donationForm.donorName);
      formData.append('businessName', donationForm.businessName || '');
      formData.append('email', donationForm.email || '');
      formData.append('phone', donationForm.phone || '');
      formData.append('address', donationForm.address);
      formData.append('pickupDate', `${year}-${month}-${day}`);
      formData.append('pickUpTime', donationForm.pickUpTime);
      formData.append('specialNote', donationForm.specialNote || '');
      formData.append('pickUpType', donationForm.pickUpType);
      formData.append('enableReminders', donationForm.enableReminders);
      formData.append('isMonetary', false);

      if (mapCoordinates) {
        formData.append('latitude', mapCoordinates.latitude);
        formData.append('longitude', mapCoordinates.longitude);
      }

      donationForm.items.forEach((item, index) => {
        if (!item.name) return;
        formData.append('items', JSON.stringify({
          name: item.name,
          description: item.description || '',
          index: index,
        }));
        if (item.files && item.files.length > 0) {
          item.files.forEach((file, fileIndex) => {
            formData.append(`item-${item.name}-${fileIndex}`, file);
          });
        }
      });

      const response = await ApiServices.postFormData('/donations', formData);
      const donationId = response.data?.donation?.id;

      if (!donationId) {
        throw new Error('No donation ID returned');
      }

      setCreatedDonationId(donationId);
      ShowSuccessToast('Donation request submitted successfully');

      setShowDeliveryContribution(true);
      return donationId;

    } catch (error) {
      console.error('Submission error:', error);
      ShowDangerToast('Failed to submit donation request');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const addItem = () => {
    setDonationForm({
      ...donationForm,
      items: [...donationForm.items, { name: '', description: '', files: [] }],
    });
  };

  const removeItem = (index) => {
    setDonationForm({
      ...donationForm,
      items: donationForm.items.filter((_, i) => i !== index),
    });
    clearItemError(index);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...donationForm.items];
    newItems[index][field] = value;
    setDonationForm({ ...donationForm, items: newItems });
    if (field === 'name' && value.trim()) {
      clearItemError(index);
    }
  };

  const handleFileChange = (index, files) => {
    const MAX_FILE_SIZE = 30 * 1024 * 1024;
    const fileArray = Array.from(files);
    const oversizedFiles = fileArray.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      ShowDangerToast(`File size must be less than 30MB. Please choose smaller files.`);
      return;
    }
    const newItems = [...donationForm.items];
    newItems[index].files = [...newItems[index].files, ...fileArray];
    setDonationForm({ ...donationForm, items: newItems });
    clearItemError(index);
  };

  const removeFile = (itemIndex, fileIndex) => {
    const newItems = [...donationForm.items];
    newItems[itemIndex].files = newItems[itemIndex].files.filter((_, i) => i !== fileIndex);
    setDonationForm({ ...donationForm, items: newItems });
    if (newItems[itemIndex].name.trim() && newItems[itemIndex].files.length === 0) {
      setItemErrors(prev => [...prev, itemIndex]);
    }
  };


  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let d = new Date(today);
    d.setDate(d.getDate() + 1);
    let count = 0;
    while (count < 16) {
      const dateStr = d.toISOString().split('T')[0];
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      const mmDd = dateStr.slice(5);
      const isUnavailable = unavailableDates.includes(dateStr) || unavailableDates.includes(mmDd) || unavailableDates.includes(dayName);
      if (!isUnavailable) {
        dates.push(new Date(d));
        count++;
      }
      d.setDate(d.getDate() + 1);
      if (d - today > 90 * 86400000) break;
    }
    return dates;
  };

  const parseTimeRange = () => {
    const amStart = store?.startTime || '9:00 AM';
    const amEnd = store?.endTime || '12:00 PM';

    const pmStart = store?.pmStartTime || '12:00 PM';
    const pmEnd = store?.pmEndTime || '5:00 PM';

    const formatTime = (timeStr) => {
      if (!timeStr) return '12:00 PM';
      const [time, period] = timeStr.trim().toUpperCase().split(' ');
      let [h, m] = time.split(':').map(Number);
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      const periodOut = h >= 12 ? 'PM' : 'AM';
      const hour = h % 12 || 12;
      const minute = m || 0;
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${periodOut}`;
    };

    return {
      amRange: `${formatTime(amStart)} - ${formatTime(amEnd)}`,
      pmRange: `${formatTime(pmStart)} - ${formatTime(pmEnd)}`,
    };
  };
  const renderStepper = () => {
    const steps = [
      { num: 1, label: 'Your Info' },
      { num: 2, label: 'Schedule' },
      { num: 3, label: 'Items' },
    ];
    return (
      <div className="flex items-center justify-between px-2 sm:px-6 py-6">
        {steps.map((step, idx) => (
          <React.Fragment key={step.num}>
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                ${currentStep > step.num
                  ? 'bg-navy-500 text-white'
                  : currentStep === step.num
                    ? 'bg-orange-400 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
              </div>
              <span className={`text-xs font-medium ${currentStep >= step.num ? 'text-navy-700' : 'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 sm:mx-4 -mt-5 ${currentStep > step.num ? 'bg-navy-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8">
      <h3 className="text-xl font-bold text-navy-800 mb-1">Your Information</h3>
      <p className="text-sm text-gray-500 mb-6">Tell us how to contact you</p>

      <div className="space-y-5">
        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">First Name *</label>
          <div className="relative">
            <User className="absolute left-3 inset-y-0 my-auto w-4 h-4 text-gray-400" />
            <input
              ref={firstNameRef}
              type="text"
              name="firstName"
              autoComplete="given-name"
              value={donationForm.firstName || ''}
              placeholder="John"
              onChange={(e) => {
                const firstName = e.target.value;
                setDonationForm({ ...donationForm, firstName, donorName: `${firstName} ${donationForm.lastName || ''}`.trim() });
                clearError('firstName');
              }}
              className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white ${errors.firstName ? 'border-red-400 ring-1 ring-red-200' : 'border border-gray-300'} text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none`}
              required
            />
          </div>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">Last Name *</label>
          <input
            ref={lastNameRef}
            type="text"
            name="lastName"
            autoComplete="family-name"
            value={donationForm.lastName || ''}
            placeholder="Smith"
            onChange={(e) => {
              const lastName = e.target.value;
              setDonationForm({ ...donationForm, lastName, donorName: `${donationForm.firstName || ''} ${lastName}`.trim() });
              clearError('lastName');
            }}
            className={`w-full px-4 py-3 rounded-xl bg-white ${errors.lastName ? 'border-red-400 ring-1 ring-red-200' : 'border border-gray-300'} text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none`}
            required
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">Business Name (optional)</label>
          <div className="relative">
            <Building2 className="absolute left-3 inset-y-0 my-auto w-4 h-4 text-gray-400" />
            <input
              type="text"
              name="organization"
              autoComplete="organization"
              value={donationForm.businessName || ''}
              placeholder="ABC Company"
              onChange={(e) => setDonationForm({ ...donationForm, businessName: e.target.value })}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">Phone *</label>
          <div className="relative">
            <Phone className="absolute left-3 inset-y-0 my-auto w-4 h-4 text-gray-400" />
            <input
              ref={phoneRef}
              type="tel"
              name="tel"
              autoComplete="tel"
              value={donationForm.phone}
              placeholder="(555) 123-4567"
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (!value.startsWith('1')) {
                  value = '1' + value;
                }
                setDonationForm({ ...donationForm, phone: `+${value}` });
                clearError('phone');
              }}
              className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white ${errors.phone ? 'border-red-400 ring-1 ring-red-200' : 'border border-gray-300'} text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none`}
            />
          </div>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">Email *</label>
          <div className="relative">
            <Mail className="absolute left-3 inset-y-0 my-auto w-4 h-4 text-gray-400" />
            <input
              ref={emailRef}
              type="email"
              name="email"
              autoComplete="email"
              value={donationForm.email}
              placeholder="john@example.com"
              onChange={(e) => {
                setDonationForm({ ...donationForm, email: e.target.value });
                clearError('email');
              }}
              className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white ${errors.email ? 'border-red-400 ring-1 ring-red-200' : 'border border-gray-300'} text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none`}
              required
            />
          </div>
          {errors.email && (
            <div className="text-red-500 text-xs mt-1">Email is required for tax receipt</div>
          )}
        </div>

        <div className="relative">
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">Pickup Address *</label>
          {isWithinGeofence === false && mapCoordinates && (
            <div className="text-orange-700 text-xs mb-2 font-medium bg-orange-50 p-3 rounded-xl border border-orange-200">
              <p className="font-semibold">No store found near this address.</p>
              <p className="mt-1">Nearest Store: <span className='text-navy-700 font-bold'>{store?.name}</span> ({distance?.toFixed(2)} miles away)</p>
              {store?.contactPhone && <p className="mt-1">Contact: {store.contactPhone}</p>}
              <p className="mt-1">Please contact them for pickup exceptions.</p>
            </div>
          )}
          <div className="relative">
            <MapPin className="absolute left-3 inset-y-0 my-auto w-4 h-4 text-gray-400" />
            <input
              ref={addressRef}
              type="text"
              name="address"
              autoComplete="street-address"
              value={donationForm.address}
              placeholder="123 Main Street, Apt 4B, City, State 12345"
              onChange={handleAddressChange}
              className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white ${errors.address ? 'border-red-400 ring-1 ring-red-200' : 'border border-gray-300'} text-gray-800 text-sm focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none`}
              autoComplete="off"
              required
            />
          </div>
          {addressSuggestions.length > 0 && (
            <ul className="absolute z-[1000] border border-gray-200 rounded-xl mt-1 w-full max-h-60 overflow-auto text-gray-800 text-sm bg-white shadow-lg">
              {addressSuggestions.map((address, index) => (
                <li
                  key={index}
                  className="px-4 py-3 cursor-pointer hover:bg-navy-50 transition"
                  onClick={() => handleAddressSelect(address)}
                >
                  {address.formattedAddress}
                </li>
              ))}
            </ul>
          )}
          {store && distance !== null && isWithinGeofence && (
            <p className='mt-2 text-xs text-gray-500'>
              Selected Store: <span className='text-navy-700 font-semibold'>{store.name}</span> ({distance.toFixed(2)} miles away)
            </p>
          )}
        </div>

        {store && (
          <button
            type="button"
            onClick={() => setShowRestrictedPopup(true)}
            className="text-sm text-navy-600 font-medium underline hover:text-navy-700 transition"
          >
            View Restricted Items
          </button>
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleNextStep}
          disabled={!store || isWithinGeofence === false}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-orange-400 text-white hover:bg-orange-500 transition shadow-sm disabled:opacity-50"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const availableDates = getAvailableDates();
    const { amRange, pmRange } = parseTimeRange();
    const selectedDateStr = donationForm.pickupDate ? donationForm.pickupDate.toISOString().split('T')[0] : null;
    const selectedDateSlots = selectedDateStr ? (availableSlots[selectedDateStr] || []) : [];
    const hasAM = selectedDateSlots.includes('AM');
    const hasPM = selectedDateSlots.includes('PM');
    const hasSlotData = Object.keys(availableSlots).length > 0;

    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8">
        <h3 className="text-xl font-bold text-navy-800 mb-1">Schedule Pickup</h3>
        <p className="text-sm text-gray-500 mb-6">Choose your preferred date and time</p>

        {slotsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 rounded-full animate-ping bg-navy-500"></div>
              <div className="w-2.5 h-2.5 rounded-full animate-ping bg-navy-400" style={{ animationDelay: '0.15s' }}></div>
              <div className="w-2.5 h-2.5 rounded-full animate-ping bg-navy-300" style={{ animationDelay: '0.3s' }}></div>
            </div>
            <span className="ml-3 text-sm text-gray-500">Loading available times...</span>
          </div>
        ) : (
          <div ref={slotPickerRef}>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Select a Date</h4>
            <div className="grid grid-cols-4 gap-3 mb-8">
              {availableDates.map((date, idx) => {
                const ds = date.toISOString().split('T')[0];
                const isSelected = selectedDateStr === ds;
                const dateSlots = availableSlots[ds] || [];
                const dateHasSlots = !hasSlotData || dateSlots.length > 0;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      if (dateHasSlots) {
                        setDonationForm({ ...donationForm, pickupDate: date, pickUpTime: '' });
                      }
                    }}
                    disabled={!dateHasSlots}
                    className={`py-3 px-2 rounded-xl border-2 text-center transition-all ${!dateHasSlots
                      ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'border-navy-500 bg-navy-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                  >
                    <div className="text-xs text-gray-500 font-medium">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className={`text-xl font-bold ${!dateHasSlots ? 'text-gray-300' : isSelected ? 'text-navy-700' : 'text-gray-800'}`}>{date.getDate()}</div>
                    <div className="text-xs text-gray-400">{date.toLocaleDateString('en-US', { month: 'short' })}</div>
                  </button>
                );
              })}
            </div>

            {selectedDateStr && (
              <>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Select a Time Window</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => (hasAM || !hasSlotData) && setDonationForm({ ...donationForm, pickUpTime: 'AM' })}
                    disabled={hasSlotData && !hasAM}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${hasSlotData && !hasAM
                      ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      : donationForm.pickUpTime === 'AM'
                        ? 'border-navy-500 bg-navy-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <Clock className={`w-5 h-5 ${donationForm.pickUpTime === 'AM' ? 'text-navy-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <div className={`font-semibold text-sm ${donationForm.pickUpTime === 'AM' ? 'text-navy-700' : 'text-gray-700'}`}>Morning</div>
                      <div className="text-xs text-gray-400">{hasSlotData && !hasAM ? 'Full' : amRange}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => (hasPM || !hasSlotData) && setDonationForm({ ...donationForm, pickUpTime: 'PM' })}
                    disabled={hasSlotData && !hasPM}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${hasSlotData && !hasPM
                      ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      : donationForm.pickUpTime === 'PM'
                        ? 'border-navy-500 bg-navy-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <Clock className={`w-5 h-5 ${donationForm.pickUpTime === 'PM' ? 'text-navy-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <div className={`font-semibold text-sm ${donationForm.pickUpTime === 'PM' ? 'text-navy-700' : 'text-gray-700'}`}>Afternoon</div>
                      <div className="text-xs text-gray-400">{hasSlotData && !hasPM ? 'Full' : pmRange}</div>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <button
            type="button"
            onClick={handlePreviousStep}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            type="button"
            onClick={handleNextStep}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-orange-400 text-white hover:bg-orange-500 transition shadow-sm"
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8">
      <h3 className="text-xl font-bold text-navy-800 mb-1">Items to Donate</h3>
      <p className="text-sm text-gray-500 mb-6">Add photos and descriptions of your items</p>

      <div className="space-y-4 mb-6">
        {donationForm.items.map((item, index) => (
          <div
            key={index}
            ref={el => itemRefs.current[index] = el}
            className={`rounded-xl border p-4 ${itemErrors.includes(index) ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">Item {index + 1}</span>
              {donationForm.items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="text-red-400 hover:text-red-600 transition"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                {item.files.length > 0 ? (
                  <div className="relative w-24 h-24">
                    <img
                      src={URL.createObjectURL(item.files[0])}
                      alt={`Item ${index}`}
                      className="w-24 h-24 object-cover rounded-xl border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index, 0)}
                      className="absolute -top-1 -right-1 bg-red-500 p-0.5 rounded-full text-white"
                    >
                      <FaTrash className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (fileInputRefs.current[index]) {
                        fileInputRefs.current[index].click();
                      }
                    }}
                    className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-navy-300 hover:text-navy-400 transition"
                  >
                    <Camera className="w-5 h-5 mb-1" />
                    <span className="text-xs">Add Photo</span>
                  </button>
                )}
                <input
                  type="file"
                  ref={el => fileInputRefs.current[index] = el}
                  multiple
                  accept="image/*,image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={e => handleFileChange(index, e.target.files)}
                />
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={e => handleItemChange(index, 'name', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-gray-300 text-sm text-gray-800 focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none"
                  placeholder="Item name (e.g., Wooden Chair)"
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={e => handleItemChange(index, 'description', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-gray-300 text-sm text-gray-800 focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none"
                  placeholder="Description (size, condition, color...)"
                />
              </div>
            </div>
            {item.files.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {item.files.slice(1).map((file, fileIndex) => (
                  <div key={fileIndex + 1} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Item ${index} extra ${fileIndex}`}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index, fileIndex + 1)}
                      className="absolute -top-1 -right-1 bg-red-500 p-0.5 rounded-full text-white"
                    >
                      <FaTrash className="w-2 h-2" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {item.name.trim() && item.files.length === 0 && (
              <div className="flex items-center text-red-500 text-xs mt-2">
                <FaExclamationCircle className="mr-1" />
                Photo is required
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addItem}
        className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-navy-300 hover:text-navy-500 transition flex items-center justify-center gap-2 text-sm font-medium"
      >
        <Plus className="w-4 h-4" />
        Add Another Item
      </button>

      <div ref={pickupTypeRef} className="mt-6">
        <label className="block mb-2 text-sm font-semibold text-gray-700">Pickup Type *</label>
        <div className="flex items-center gap-6">
          <label className="flex items-center cursor-pointer gap-2">
            <input
              type="radio"
              name="pickUpType"
              value="residential"
              checked={donationForm.pickUpType === 'residential' || donationForm.pickUpType === 'Residential'}
              onChange={e => setDonationForm({ ...donationForm, pickUpType: e.target.value })}
              className="accent-navy-500 w-4 h-4"
            />
            <span className="text-sm text-gray-700">Residential</span>
          </label>
          <label className="flex items-center cursor-pointer gap-2">
            <input
              type="radio"
              name="pickUpType"
              value="corporate"
              checked={donationForm.pickUpType === 'corporate' || donationForm.pickUpType === 'Corporate'}
              onChange={e => setDonationForm({ ...donationForm, pickUpType: e.target.value })}
              className="accent-navy-500 w-4 h-4"
            />
            <span className="text-sm text-gray-700">Corporate</span>
          </label>
        </div>
      </div>

      <div className="mt-4">
        <label className="block mb-1.5 text-sm font-semibold text-gray-700">Pickup Instructions</label>
        <textarea
          value={donationForm.specialNote}
          onChange={e => setDonationForm({ ...donationForm, specialNote: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-800 focus:ring-2 focus:ring-navy-300 focus:border-navy-400 transition outline-none resize-y min-h-[80px]"
          placeholder="Any special instructions for pickup..."
        />
      </div>

      <div className="mt-4">
        <label className="flex items-center cursor-pointer gap-2">
          <input
            type="checkbox"
            checked={donationForm.enableReminders}
            onChange={(e) => setDonationForm({ ...donationForm, enableReminders: e.target.checked })}
            className="accent-navy-500 w-4 h-4 rounded"
          />
          <span className="text-sm text-gray-700">Send me reminder emails</span>
        </label>
      </div>

      <div className="flex items-center justify-between mt-8">
        <button
          type="button"
          onClick={handlePreviousStep}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-orange-400 text-white hover:bg-orange-500 transition shadow-sm disabled:opacity-50"
        >
          {isSubmitting ? (
            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          ) : null}
          {isSubmitting ? 'Submitting...' : 'Submit Donation Request'}
        </button>
      </div>
    </div>
  );

  const renderThankYou = () => (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white flex flex-col items-center justify-start">
      <div className="w-full bg-navy-500 py-10 sm:py-14 flex flex-col items-center text-center px-4">
        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4">
          <Check className="w-8 h-8 text-navy-500" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Thank You!</h2>
        <p className="text-navy-100 text-sm sm:text-base">Your donation request has been submitted</p>
      </div>

      <div className="w-full max-w-lg px-4 -mt-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 text-center">
          <Heart className="w-8 h-8 text-orange-300 mx-auto mb-3" />
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
            We appreciate your generosity! Our team will review your donation request and contact you to confirm your pickup.
          </p>

          <div className="bg-gray-50 rounded-xl p-5 text-left mb-6">
            <h4 className="font-bold text-gray-800 mb-4">What happens next?</h4>
            <div className="space-y-4">
              {[
                "We'll review your donation items",
                'A driver will be assigned to your pickup',
                "You'll receive a confirmation with pickup details",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-navy-100 text-navy-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-sm text-gray-600">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {(store?.contactPhone || store?.contactEmail) && (
            <div className="text-left mb-6">
              <p className="text-sm text-gray-500 mb-2">Questions? Contact us:</p>
              {store?.contactPhone && (
                <a href={`tel:${store.contactPhone}`} className="flex items-center gap-2 text-sm text-navy-600 font-medium mb-1 hover:underline">
                  <Phone className="w-4 h-4" />
                  {store.contactPhone}
                </a>
              )}
              {store?.contactEmail && (
                <a href={`mailto:${store.contactEmail}`} className="flex items-center gap-2 text-sm text-navy-600 font-medium hover:underline">
                  <Mail className="w-4 h-4" />
                  {store.contactEmail}
                </a>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setShowThankYou(false);
              setShowDeliveryContribution(false);
              setCurrentStep(1);
              setDonationForm({
                donorName: '',
                firstName: '',
                lastName: '',
                businessName: '',
                email: '',
                phone: '',
                address: '',
                pickupDate: null,
                pickUpTime: '',
                specialNote: '',
                items: [{ name: '', description: '', files: [] }],
                pickUpType: '',
                enableReminders: false,
              });
              setMapCoordinates(null);
              setIsWithinGeofence(null);
              setTravelTimePolygon([]);
              setStore(null);
              setCandidateStores([]);
              setDistance(null);
              setErrors({});
              setItemErrors([]);
              setDeliveryContribution({
                contribute: false,
                amount: '',
                presetAmount: '500',
              });
              setPaymentError(null);
              setCreatedDonationId(null);
            }}
            className="w-full py-3 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            Close This Page
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="rounded-2xl p-8 bg-white shadow-lg border border-gray-100">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-3 h-3 rounded-full animate-ping bg-navy-500"></div>
            <div className="w-3 h-3 rounded-full animate-ping bg-navy-400" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-3 h-3 rounded-full animate-ping bg-navy-300" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="mt-4 text-base font-medium text-gray-600 text-center">Loading...</p>
        </div>
      </div>
    );
  }

  if (showThankYou) {
    return renderThankYou();
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-4">
        {showDeliveryContribution && (
          <Elements stripe={stripePromise}>
            <DeliveryContributionForm
              deliveryContribution={deliveryContribution}
              setDeliveryContribution={setDeliveryContribution}
              processingPayment={processingPayment}
              setProcessingPayment={setProcessingPayment}
              paymentError={paymentError}
              setPaymentError={setPaymentError}
              createdDonationId={createdDonationId}
              donationForm={donationForm}
              setShowThankYou={setShowThankYou}
              setShowDeliveryContribution={setShowDeliveryContribution}
              ShowDangerToast={ShowDangerToast}
              ShowSuccessToast={ShowSuccessToast}
              ApiServices={ApiServices}
            />
          </Elements>
        )}

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-navy-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-navy-800">{store?.name || 'Community Thrift Store'}</h1>
            <p className="text-xs text-gray-500">Schedule a Donation Pickup</p>
          </div>
        </div>

        {renderStepper()}

        <form onSubmit={(e) => {
          e.preventDefault();
          createDonationRequest();
        }}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && store && isWithinGeofence && renderStep2()}
          {currentStep === 3 && store && isWithinGeofence && renderStep3()}
        </form>
      </div>

      {showRestrictedPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Restricted Items</h3>
            {restrictedItem.length > 0 ? (
              <ul className="list-disc list-inside text-left text-gray-700 text-sm font-medium space-y-2 pl-4">
                {restrictedItem.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600 text-sm">No restricted items defined for this store.</p>
            )}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowRestrictedPopup(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-navy-500 text-white hover:bg-navy-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationRequestPage;